<template>
  <div
    id="menu"
    ref="menuEl"
    :class="[isMenuExpanded ? 'expanded' : 'collapsed']"
    :style="menuStyle"
  >
    <MenuPrimary ref="menuPrimaryEl" @search="$emit('search')" />
    <MenuSecondary @toggle-menu-expand="$emit('toggleMenuExpand')" />
    <div
      v-if="isMenuExpanded"
      class="menu-resize-handle"
      @mousedown="startResize"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch } from "vue";

import * as inject from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import {
  MENU_WIDTH_EXPANDED_MAX,
  MENU_WIDTH_EXPANDED_MIN,
} from "@/utils/constants";

import MenuPrimary from "./MenuPrimary.vue";
import MenuSecondary from "./MenuSecondary.vue";

const clampExpandedWidth = (value: number): number => {
  if (!Number.isFinite(value)) return MENU_WIDTH_EXPANDED_MIN;
  return Math.min(
    MENU_WIDTH_EXPANDED_MAX,
    Math.max(MENU_WIDTH_EXPANDED_MIN, value)
  );
};

export default defineComponent({
  components: {
    MenuPrimary,
    MenuSecondary,
  },
  emits: {
    toggleMenuExpand: () => true,
    search: () => true,
  },
  setup() {
    const menu = inject.menu();
    const isMenuExpanded = menu.isExpanded;
    const menuPrimaryEl = ref<InstanceType<typeof MenuPrimary>>();
    const menuEl = ref<HTMLElement>();
    const profile = inject.profile();
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const expandedWidth = releaser.add(
      syncState<number>("menu:width", MENU_WIDTH_EXPANDED_MIN)
    );

    const clampedExpandedWidth = computed(() =>
      clampExpandedWidth(expandedWidth.value)
    );
    const menuStyle = computed(() =>
      isMenuExpanded.value
        ? { width: `${clampedExpandedWidth.value}px` }
        : undefined
    );
    watch(
      clampedExpandedWidth,
      (width) => {
        menu.expandedWidth.value = width;
      },
      { immediate: true }
    );

    const resize = (event: MouseEvent) => {
      if (!menuEl.value || !isMenuExpanded.value) return;
      const rect = menuEl.value.getBoundingClientRect();
      const rawWidth =
        document.documentElement.dir === "rtl"
          ? rect.right - event.clientX
          : event.clientX - rect.left;
      expandedWidth.value = clampExpandedWidth(rawWidth);
    };
    const stopResize = () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
    };
    const startResize = (event: MouseEvent) => {
      if (!isMenuExpanded.value) return;
      event.preventDefault();
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResize);
    };
    onBeforeUnmount(stopResize);

    return {
      RouteNames,
      isMenuExpanded,
      menuEl,
      menuPrimaryEl,
      menuStyle,
      startResize,
    };
  },
});
</script>

<style lang="scss">
// WARNING: Carrefull CSS non scoped and will be applied to the whole app
@use "@/styles/menu.scss";
</style>
